import React, { useState } from "react";
import Button from "../../../components/common/Button";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { MdArrowBack, MdEdit, MdDelete } from "react-icons/md";
import Card from "components/card";
import { useRecipe, useRecipeOperations } from "../../../hooks/useRecipes";
import { useImageUpload } from "../../../hooks/useImageUpload";
import { UpdateRecipeInput } from "../../../types/recipe";
import { RecipeService } from "../../../services/recipeService";
import RecipeModal from "../../../components/recipes/RecipeModal";
import RecipeComponentsList from "../../../components/recipes/RecipeComponentsList";
import RecipeDirections from "../../../components/recipes/RecipeDirections";
import RecipeHeaderCard from "../../../components/recipes/RecipeHeaderCard";
import RecipeNutritionSummary from "../../../components/recipes/RecipeNutritionSummary";
import RecipeInfoCard from "../../../components/recipes/RecipeInfoCard";
import DeleteConfirmationModal from "../../../components/modals/DeleteConfirmationModal";
import { toRecipeInput } from "../../../utils/recipeUtils";
import LoadingSpinner from "components/common/LoadingSpinner";

const RecipeDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const recipeId = id ? parseInt(id, 10) : null;
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const { recipe, loading, error, refresh } = useRecipe(recipeId);

  const image = useImageUpload({
    upload: recipe
      ? (file) => RecipeService.uploadRecipeImage(recipe.id, file)
      : null,
    remove: recipe?.recipe_image?.id
      ? () => RecipeService.deleteRecipeImage(recipe.id, recipe.recipe_image.id)
      : null,
    hasImage: !!recipe?.recipe_image,
    onUploaded: refresh,
    onDeleted: refresh,
  });
  const {
    loading: operationLoading,
    updateRecipe,
    deleteRecipe,
    error: operationError,
  } = useRecipeOperations();

  const handleBack = () => {
    const returnPath =
      "/admin/nutrition" + ((location.state as any)?.from || "");
    navigate(returnPath);
  };

  const handleEdit = () => {
    if (recipe) {
      setShowEditModal(true);
    }
  };

  const handleDelete = () => {
    setShowDeleteModal(true);
  };

  const confirmDeleteRecipe = async () => {
    if (!recipe) return;

    const success = await deleteRecipe(recipe.id);
    if (success) {
      const returnPath =
        "/admin/nutrition" + ((location.state as any)?.from || "");
      navigate(returnPath);
    }
  };

  const handleCloseDeleteModal = () => {
    setShowDeleteModal(false);
  };

  const handleUpdateSubmit = async (recipeData: any) => {
    if (!recipe) return;

    const updateData: UpdateRecipeInput = toRecipeInput(recipeData);

    const updatedRecipe = await updateRecipe(recipe.id, updateData);
    if (updatedRecipe) {
      refresh(); // Refresh the current recipe data
      setShowEditModal(false);
    }
  };

  const handleCloseEditModal = () => {
    setShowEditModal(false);
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  if (error || !recipe) {
    return (
      <Card extra="w-full p-3 md:p-6">
        <div className="py-12 text-center">
          <div className="mb-4 text-lg text-red-500">
            {error || "Recipe not found"}
          </div>
          <button
            onClick={handleBack}
            className="inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50"
          >
            <MdArrowBack className="h-6 w-6 md:mr-2 md:h-4 md:w-4" />
            <span className="hidden md:inline">Back to Recipes</span>
          </button>
        </div>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between">
        <button
          onClick={handleBack}
          className="inline-flex items-center px-3 py-2 text-sm font-medium text-gray-600 transition-colors hover:text-gray-900 dark:text-gray-300 dark:hover:text-white"
        >
          <MdArrowBack className="h-6 w-6 md:mr-2 md:h-4 md:w-4" />
          <span className="hidden md:inline">Back to Recipes</span>
        </button>

        <div className="mt-3 flex flex-wrap justify-end gap-3">
          <Button onClick={handleEdit} disabled={operationLoading}>
            <MdEdit className="mr-2 h-4 w-4" />
            Edit Recipe
          </Button>
          <button
            onClick={handleDelete}
            disabled={operationLoading}
            className="inline-flex items-center rounded-md bg-red-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-red-700 disabled:opacity-50"
          >
            <MdDelete className="mr-2 h-4 w-4" />
            Delete Recipe
          </button>
        </div>
      </div>

      <RecipeHeaderCard
        recipe={recipe}
        uploadingImage={image.uploading}
        deletingImage={image.deleting}
        imageError={image.error}
        fileInputRef={image.fileInputRef}
        onImageUploadClick={image.triggerUpload}
        onDeleteImageClick={image.openDeleteModal}
        onImageFileChange={image.handleFileChange}
      />

      <RecipeNutritionSummary recipe={recipe} />

      {recipe.components && recipe.components.length > 0 && (
        <Card extra="w-full p-3 md:p-6">
          <h2 className="mb-6 text-xl font-bold text-navy-700 dark:text-white">
            Ingredients
          </h2>
          <RecipeComponentsList components={recipe.components} />
        </Card>
      )}

      {recipe.directions && (
        <Card extra="w-full p-3 md:p-6">
          <h2 className="mb-6 text-xl font-bold text-navy-700 dark:text-white">
            Cooking Directions
          </h2>
          <RecipeDirections directions={recipe.directions} />
        </Card>
      )}

      {recipe.note && (
        <Card extra="w-full p-3 md:p-6">
          <h2 className="mb-4 text-xl font-bold text-navy-700 dark:text-white">
            Note
          </h2>
          <p className="whitespace-pre-line leading-relaxed text-gray-700 dark:text-gray-300">
            {recipe.note}
          </p>
        </Card>
      )}

      <RecipeInfoCard recipe={recipe} />

      {operationError && (
        <Card extra="w-full p-4">
          <div className="rounded-md border border-red-200 bg-red-50 p-4 dark:border-red-700 dark:bg-red-900">
            <div className="text-red-800 dark:text-red-200">
              Error: {operationError}
            </div>
          </div>
        </Card>
      )}

      <RecipeModal
        isOpen={showEditModal}
        onClose={handleCloseEditModal}
        onSubmit={handleUpdateSubmit}
        recipe={recipe}
        mode="edit"
        loading={operationLoading}
        error={operationError}
      />

      <DeleteConfirmationModal
        isOpen={showDeleteModal}
        onClose={handleCloseDeleteModal}
        onConfirm={confirmDeleteRecipe}
        title="Delete Recipe"
        message="Are you sure you want to delete this recipe? This action cannot be undone."
        itemName={recipe?.name || "Recipe"}
        loading={operationLoading}
        error={operationError}
      />

      <DeleteConfirmationModal
        isOpen={image.showDeleteModal}
        onClose={image.closeDeleteModal}
        onConfirm={image.confirmDelete}
        title="Delete Recipe Image"
        message="Are you sure you want to delete this recipe image? This action cannot be undone."
        itemName={recipe?.name || "Recipe"}
        loading={image.deleting}
        error={image.error}
      />
    </div>
  );
};

export default RecipeDetail;
